import classNames from "classnames";
import type { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { FlagshipReviewsBlock as FlagshipReviewsBlockValue } from "@reactivated";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { STAR_SETS, StarSVG } from "../../apps/retail-locator/StarRating";

/**
 * Named ``FlagshipReviewsBlock`` (not ``ReviewsBlock``) to avoid colliding
 * with the toolbox's ``ReviewsBlock`` in the flat block-type namespace; the
 * StreamField key stays ``reviews``.
 *
 * ``page.title`` (the lead-in headline) is PAGE-level, so it arrives as the
 * ``page_title`` prop from the page body's ``flagship_reviews_ssr``
 * side-channel, injected raw into the headline so any HTML entities in the
 * title render correctly.
 *
 * Read-more split: reviews 1-2 are always visible; reviews 3+ live in a
 * hidden readmore target. The global ``[data-section-toggle]``
 * DOMContentLoaded handler is unreliable for hydrated island nodes, so the
 * expand/collapse is driven by component state instead, while keeping the
 * same ``data-section-toggle`` / ``data-section-toggle-target`` attributes
 * and ``--is-active`` class semantics. When fewer than 3 reviews exist, no
 * toggle/target is emitted.
 *
 * Each review renders a header (5 star SVGs by ``rating``), body
 * (``pullquote`` plain text + ``quote`` richtext -- a ``RichTextBlock``
 * already serialized with its ``<div class="rich-text">`` wrapper, injected
 * raw) and footer (``author`` + ``location``).
 */

type Review = NonNullable<
    NonNullable<FlagshipReviewsBlockValue["reviews"]>[number]
>;

const ReviewCard = (props: Immutable<{ item: Review }>): React.JSX.Element => {
    const item = props.item;
    const stars = item.rating ? STAR_SETS[item.rating] : undefined;
    return (
        <div className="reviews__review">
            <div className="reviews__header">
                {stars
                    ? stars.map((path, i) => <StarSVG key={i} path={path} />)
                    : null}
            </div>
            <div className="reviews__body">
                {item.pullquote ? (
                    <div className="reviews__quote">
                        <p>
                            {"“"}
                            {item.pullquote}
                            {"”"}
                        </p>
                    </div>
                ) : null}
                {item.quote ? (
                    <div
                        className="reviews__copy"
                        dangerouslySetInnerHTML={{ __html: item.quote }}
                    />
                ) : null}
            </div>
            <div className="reviews__footer">
                {item.author ? (
                    <div className="reviews__author">{item.author}</div>
                ) : null}
                {item.location ? (
                    <div className="reviews__location">{item.location}</div>
                ) : null}
            </div>
        </div>
    );
};

export const FlagshipReviewsBlock = (
    props: Immutable<{ value: FlagshipReviewsBlockValue; page_title: string }>,
): React.JSX.Element => {
    const reviews = props.value.reviews ?? [];
    const [expanded, setExpanded] = React.useState(false);

    // Split at the 3rd review: reviews 1-2 are always visible; reviews 3+
    // live in the readmore target.
    const visible = reviews.slice(0, 2);
    const hidden = reviews.slice(2);
    const hasMore = hidden.length > 0;

    const onToggle = (event: React.SyntheticEvent) => {
        event.preventDefault();
        setExpanded((prev) => !prev);
    };

    const onToggleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setExpanded((prev) => !prev);
        }
    };

    return (
        <div
            className="reviews-container l-capped-width  u-margin-bottom--xl"
            id="reviews"
        >
            <div className="lead-in">
                <h1
                    className="lead-in__headline lead-in__headline--line"
                    dangerouslySetInnerHTML={{
                        __html: interpolate(t`%(page_title)s Reviews`, {
                            page_title: props.page_title,
                        }),
                    }}
                />
            </div>

            <div className="reviews">
                {visible.map((item, i) => (
                    <ReviewCard key={i} item={item} />
                ))}
            </div>

            {hasMore ? (
                <>
                    <div
                        className={classNames(
                            "reviews__readmore-toggle",
                            "l-capped-width",
                            {
                                "reviews__readmore-toggle--is-active": expanded,
                            },
                        )}
                        role="link"
                        aria-expanded={expanded ? "true" : "false"}
                        tabIndex={0}
                        data-section-toggle
                        onClick={onToggle}
                        onKeyDown={onToggleKeyDown}
                    >
                        <div className="reviews__readmore">
                            {interpolate(t`More %(page_title)s Reviews`, {
                                page_title: props.page_title,
                            })}
                            <br />
                            <span className="reviews__readmore-arrow"></span>
                        </div>
                    </div>
                    <div
                        className={classNames(
                            "reviews__readmore-target",
                            "reviews",
                            {
                                "reviews__readmore-target--is-active": expanded,
                            },
                        )}
                        data-section-toggle-target
                    >
                        {hidden.map((item, i) => (
                            <ReviewCard key={i} item={item} />
                        ))}
                    </div>
                </>
            ) : null}
        </div>
    );
};
